/**
 * Your schedules of one report: made in chat (or Workflows), paused, resumed
 * and deleted here. Always the caller's own; the API enforces it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

const scheduleSchema = z
  .object({
    id: z.string(),
    paused: z.boolean(),
    customer: z.string().nullable(),
    region: z.string().nullable(),
    units: z.string(),
    period: z.string(),
    schedule: z.union([
      z.object({ days: z.array(z.number()), time: z.string() }),
      z.object({ monthDays: z.array(z.number()), time: z.string() }),
    ]),
    recipients: z.array(z.object({ id: z.string(), name: z.string() })),
  })
  .passthrough();
export type ReportSchedule = z.infer<typeof scheduleSchema>;

export const useReportSchedules = (templateId: string, enabled: boolean) => {
  const queryClient = useQueryClient();
  const schedules = useQuery({
    queryKey: queryKeys.reportSchedules(templateId),
    queryFn: async () =>
      z
        .array(scheduleSchema)
        .parse(await apiRequest<unknown>(`/api/report-schedules?templateId=${templateId}`)),
    enabled,
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.reportSchedules(templateId) });

  const setPaused = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      apiRequest<unknown>(`/api/report-schedules/${id}`, { method: 'PATCH', body: { paused } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest<unknown>(`/api/report-schedules/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  return { schedules, setPaused, remove };
};
